import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, ConfigProvider, DatePicker, Input, InputNumber, Modal, Row, Select, Tag, Tooltip } from 'antd';
import { PricingTemplateData } from '../interfaces/interfaces';
import dayjs, { Dayjs } from 'dayjs';
import { LinkOutlined, WarningOutlined } from '@ant-design/icons';
import LinkTemplateToHubspot from './LinkTemplateToHubspot';

interface TemplateDetailsProps {
    readOnly?: boolean;
    dataSrc: PricingTemplateData;
    pricingContributors: any;
    onEdit: (record: any) => void;
    templateId?: number;
    showHubspotLink?: boolean;
    pricingVersion?: any[];
    onBasePriceVersionChange?: (versionId: number) => void;
    onUseOldWorkhoursChange?: (value: boolean) => void;
}

const { Option } = Select;
const { TextArea } = Input;

const PricingTemplateDetails: React.FC<TemplateDetailsProps> = ({ dataSrc, pricingContributors, onEdit, templateId, showHubspotLink = false, pricingVersion = [], onBasePriceVersionChange, onUseOldWorkhoursChange, readOnly = false }) => {
    const [dealContributors, setDealContributors] = useState<any>(dataSrc['Pricing Contributors'] || []);
    const [engagementType, setEngagementType] = useState<string | null>(dataSrc['Engagement Type'] || null);
    const [templateName, setTemplateName] = useState<string>(dataSrc['Template Name'] || '');
    const [templateDescription, setTemplateDescription] = useState<string>(dataSrc['Template Description'] || '');
    const [expectedStartDate, setExpectedStartDate] = useState<Dayjs>(dayjs(dataSrc['Expected Start Date'] || ''));
    const [duration, setDuration] = useState<number | null>(Number(dataSrc['Duration'] || null));
    const [basePriceVersionId, setBasePriceVersionId] = useState<number>(dataSrc['Base Price Version ID'] || 2);
    const [useOldWorkhours, setUseOldWorkhours] = useState<boolean>(dataSrc['Use Old Workhours'] || false);
    const engagementTypeOptions = ['Fixed Fee', 'Retainer', 'Time & Materials'];
    const [openHubspotLink, setOpenHubspotLink] = useState(false)

    const latestVersionId = useMemo(() => {
        if (pricingVersion.length === 0) return null;
        return Math.max(...pricingVersion.map((v: any) => v.id));
    }, [pricingVersion]);

    const isLatestVersion = latestVersionId === null || basePriceVersionId === latestVersionId;
    const isLinkReady = isLatestVersion;

    useEffect(() => {
        const versionName = pricingVersion.length > 0
            ? pricingVersion.find((v: any) => v.id === basePriceVersionId)?.name || dataSrc['Base Price Version Name'] || ''
            : dataSrc['Base Price Version Name'] || '';
        const updatedData = {
            'Template Name': templateName,
            'Template Description': templateDescription,
            'Engagement Type': engagementType || '',
            'Expected Start Date': expectedStartDate.format('YYYY-MM-DD'),
            'Duration': duration,
            'Pricing Contributors': dealContributors,
            'Base Price Version ID': basePriceVersionId,
            'Base Price Version Name': versionName,
            'Use Old Workhours': useOldWorkhours
        };
        onEdit(updatedData);
    }, [templateName, templateDescription, engagementType, expectedStartDate, duration, dealContributors, basePriceVersionId, useOldWorkhours]);

    const renderInputField = (key: string, value: any) => {
        switch (key) {
            case 'Template Description':
                return (
                    <TextArea
                        placeholder={`Enter ${key}`}
                        value={templateDescription}
                        autoSize={{ minRows: 2, maxRows: 5 }}
                        style={{ width: "70%", whiteSpace: "pre-wrap" }}
                        onChange={(e) => setTemplateDescription(e.target.value)}
                    />
                );
            case 'Pricing Contributors':
                return pricingContributors.length > 0 && (
                    <Tooltip title="Users who are added as Pricing Contributors can view and edit all pricing versions for the deal. They cannot submit pricing for ODE locking.">
                        <Select
                            mode="multiple"
                            placeholder="Add Pricing Contributors"
                            allowClear
                            style={{ width: "70%", marginBottom: "10px" }}
                            value={dealContributors.map((contributor: any) => contributor.email)}
                            onChange={(value) => {
                                const selectedContributors = pricingContributors.filter((contributor: any) => value.includes(contributor.email));
                                setDealContributors(selectedContributors);
                            }}
                        >
                            {pricingContributors.map((option: any, index: number) => (
                                <Option key={index} value={option.email}>
                                    {option.name} - {option.email}
                                </Option>
                            ))}
                        </Select>
                    </Tooltip>
                );
            case 'Engagement Type':
                return (
                    <Select
                        placeholder="Select Engagement Type"
                        style={{ width: "70%", marginBottom: "10px" }}
                        value={engagementType}
                        onChange={(value) => setEngagementType(value)}
                    >
                        {engagementTypeOptions.map((option, index) => (
                            <Option key={index} value={option}>
                                {option}
                            </Option>
                        ))}
                    </Select>
                );
            case 'Expected Start Date':
                return (
                    <DatePicker
                        value={expectedStartDate}
                        allowClear={false}
                        style={{ width: '70%' }}
                        onChange={(value) => setExpectedStartDate(value)}
                    />
                );
            case 'Duration':
                return (
                    <InputNumber
                        placeholder={`Enter ${key}`}
                        min={1}
                        max={36}
                        step={0.1}
                        suffix="months"
                        value={duration}
                        style={{ width: "70%", whiteSpace: "pre-wrap" }}
                        onChange={(value) => setDuration(value)}
                    />
                );
            case 'Template Name':
                return (
                    <Input
                        placeholder={`Enter ${key}`}
                        value={templateName}
                        size="large"
                        style={{ width: "70%", whiteSpace: "pre-wrap" }}
                        onChange={(e) => setTemplateName(e.target.value)}
                    />
                );
            case 'Use Old Workhours':
                return (
                    <Tooltip title="Work hours are fixed to 8 hrs/day x working days as per latest requirements.">
                        <Select
                            disabled
                            style={{ width: "70%" }}
                            value={'No'}
                        >
                            <Option value="No">No (8 hrs/day x working days)</Option>
                        </Select>
                    </Tooltip>
                );
            case 'Base Price Version Name':
                return pricingVersion.length > 0 ? (
                    <Tooltip title={!isLatestVersion ? "You are using an older pricing version. Please select the latest version before linking to a deal." : ""}>
                        <Select
                            status={!isLatestVersion ? 'error' : undefined}
                            style={{ width: "70%" }}
                            value={basePriceVersionId}
                            onChange={(value) => {
                                setBasePriceVersionId(value);
                                onBasePriceVersionChange?.(value);
                            }}
                        >
                            {pricingVersion.map((version: any) => (
                                <Option key={version.id} value={version.id}>
                                    {version.name} {version.id === latestVersionId && <Tag color="green" style={{ marginLeft: 8 }}>Latest</Tag>}
                                </Option>
                            ))}
                        </Select>
                    </Tooltip>
                ) : (
                    <div>{value}</div>
                );
            default:
                return null;
        }
    };

    return (
        <Card title="Template Details" style={{ marginBottom: '16px' }}
            extra={showHubspotLink && (
                <Tooltip title={!isLinkReady ? 'Select the latest Base Price Version before linking to a deal.' : ''}>
                    <Button type='primary' icon={<LinkOutlined />}
                        disabled={!isLinkReady}
                        danger={!isLinkReady}
                        onClick={()=>setOpenHubspotLink(true)}
                    >
                        {!isLinkReady && <WarningOutlined />} Link to HubSpot Deal
                    </Button>
                </Tooltip>
            )}
        > 
            <ConfigProvider componentDisabled={readOnly}>
            <Row align="middle" gutter={[16, 16]}>
                {Object.entries(dataSrc).map(([key, value], index) => (
                    <>
                    {key !== 'Base Price Version ID' && <Col span={10} key={index}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                            {key !== 'Base Price Version ID' && (key !== 'Pricing Contributors' || pricingContributors.length > 0) && (
                                <label style={{ marginRight: "8px", width: "35%" }}>
                                    {key === 'Template Description' || key === 'Pricing Contributors' || key === 'Base Price Version Name' || key === 'Use Old Workhours' ? key : <span>{key} *</span>}
                                </label>
                            )}
                            {renderInputField(key, value)}
                        </div>
                    </Col>
                    }</>
                ))}
            </Row>
            </ConfigProvider>
            {openHubspotLink && 
            <Modal open={openHubspotLink} 
                width={'66%'}
                onCancel={()=>setOpenHubspotLink(false)} footer={null}>
                <LinkTemplateToHubspot pricingTemplateData={dataSrc} closeModal={setOpenHubspotLink} templateId={templateId}/>
            </Modal>}
        </Card>
    );
};

export default PricingTemplateDetails;
